'use client'

import { Item } from '@/app/api/types/redpim.types'
import DatoLink from '@/components/DatoLink/DatoLink'
import { LocalizedLink } from '@/components/LocalizedLink'
import { InternalOrExternalLinkFragment } from '@/graphql/fragments/ExternalOrInternalLink.generated'
import { ProductDetailRecord } from '@/graphql/types'
import { useUnitContext } from '@/hooks/useUnitContext'
import { getProductDocumentsBySerialNumber } from '@/lib/digital-twin'
import { getInlineSpecsByUnit } from '@/utils/getSpecsFromItemSpecificationBlocs'
import { Button, SelectButton } from '@desoutter/uikit'
import SearchInput from '@desoutter/uikit/src/components/atoms/SearchInput'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useCallback, useState } from 'react'
import { ProductDetailsStickyTranslationsFragment } from './ProductDetailsSticky.generated'

type ProductDetailsDescriptionProps = {
	lang: string
	item: Item
	items?: Item[]
	fragment: ProductDetailRecord
	translations: ProductDetailsStickyTranslationsFragment
}
export default function ProductDetailsSticky({
	lang,
	item,
	items,
	fragment,
	translations,
}: ProductDetailsDescriptionProps) {
	const selectItem = items?.find((el) => el.partnumber === item.partnumber)
	const router = useRouter()
	const { unit } = useUnitContext()
	const [serialNumber, setSerialNumber] = useState('')
	const [isSearching, setIsSearching] = useState(false)
	const [searchError, setSearchError] = useState('')

	const handleSerialNumberSearch = useCallback(
		async (e) => {
			e.preventDefault()

			if (!serialNumber.trim()) {
				setSearchError('Please enter a serial number')
				return
			}

			setIsSearching(true)
			setSearchError('')

			try {
				const data = await getProductDocumentsBySerialNumber(serialNumber, lang)

				// Store serial number in localStorage for future use
				localStorage.setItem('sn', serialNumber)

				// Redirect to digital twin details page
				router.push(
					`/${lang}/digital-twin/${data.partNumber}?sn=${serialNumber}`,
				)
			} catch (error) {
				setSearchError('No digital twin found with this serial number')
				console.error(error)
			} finally {
				setIsSearching(false)
			}
		},
		[serialNumber, lang, router],
	)

	return (
		<div className="container-auto xl:sticky flex justify-end top-8 xl:top-40 right-0 xl:h-0 xl:mb-[1950px] z-30">
			{selectItem && (
				<div className="w-full xl:w-[500px]">
					<div
						className="bg-white rounded-2xl p-8"
						style={{
							boxShadow: '0px 12px 24px 0px rgba(0, 0, 0, 0.08)',
						}}
					>
						<div className="h4">{item?.model}</div>
						{item?.shortDescriptionsIntl && (
							<p>{item?.shortDescriptionsIntl[lang]}</p>
						)}
						{items?.length > 1 && (
							<SelectButton
								className="mb-6"
								selectedElem={
									<LocalizedLink
										locale={lang}
										href={`/products/${selectItem.partnumber}`}
										className="flex"
										rel="nofollow"
									>
										{selectItem.thumbnail && (
											<Image
												src={
													selectItem.thumbnail
														? new URL(selectItem.thumbnail).toString() +
															'/jcr:content/renditions/cq5dam.web.400.400.jpeg'
														: '/images/image-placeholder.svg'
												}
												width="40"
												className="object-contain"
												height="40"
												alt=""
											/>
										)}
										<div className="ml-2 pr-6 overflow-auto">
											<p className="text-md font-bold truncate">
												{selectItem.model}
											</p>
											<p>
												{selectItem.specificationBlocks
													? getInlineSpecsByUnit(
															selectItem.specificationBlocks,
															unit,
														)
													: selectItem.partnumber}
											</p>
										</div>
									</LocalizedLink>
								}
							>
								{items?.map((el, i) => (
									<li
										className="border-b last-of-type:border-b-0"
										key={`${el.partnumber}-${i}`}
									>
										<LocalizedLink
											locale={lang}
											prefetch={false}
											href={`/products/${el.partnumber}`}
											className="flex w-full px-4 py-2"
											rel="nofollow"
										>
											{!!el.thumbnail && (
												<Image
													src={
														el.thumbnail
															? new URL(
																	el.thumbnail +
																		'/jcr:content/renditions/cq5dam.web.400.400.jpeg',
																).toString()
															: '/images/image-placeholder.svg'
													}
													width="40"
													height="40"
													alt={el.model}
												/>
											)}
											<div className="ml-2">
												<p className="w-80 text-md font-bold truncate">
													{el.model}
												</p>
												<p>
													{el.specificationBlocks
														? getInlineSpecsByUnit(el.specificationBlocks, unit)
														: el.partnumber}
												</p>
											</div>
										</LocalizedLink>
									</li>
								))}
							</SelectButton>
						)}
						<div className="pt-6 border-t border-gray-100">
							<DatoLink
								fragment={fragment.redButton as InternalOrExternalLinkFragment}
								params={`?pn=${item.partnumber}`}
								className="productStickyQuote"
							>
								<button className="btn primary md w-full justify-center">
									{fragment.redButton.label}
								</button>
							</DatoLink>
							<div className="flex justify-between items-center mt-4">
								{fragment.buttons.map((button, i) => (
									<DatoLink
										fragment={button as InternalOrExternalLinkFragment}
										params={
											`?${
												button.__typename === 'InternalLinkRecord'
													? 'pn'
													: 'search'
											}=` + item.partnumber
										}
										className={i ? 'productStickyBuy' : 'productStickyQuestion'}
										key={button.id}
									>
										<Button
											variant="outline"
											color="tertiary"
											title={button.label}
										>
											{button.label}
										</Button>
									</DatoLink>
								))}
							</div>
						</div>
					</div>

					<div className="mt-4">
						<div
							className="bg-white rounded-2xl px-8 py-6"
							style={{
								boxShadow: '0px 12px 24px 0px rgba(0, 0, 0, 0.08)',
							}}
						>
							<h3>{fragment.getYourProductInformationsTitle}</h3>
							<p>{fragment.getYourProductInformationsText}</p>
							<form onSubmit={handleSerialNumberSearch} className="mb-3 mt-6">
								<label
									htmlFor="serialNumber"
									className="text-sm font-medium mb-1 block"
								>
									{translations.searchLabel}
								</label>
								<SearchInput
									label={translations.searchLabel}
									placeholder={translations.serialNumber}
									setSearch={setSerialNumber}
									isLoading={isSearching}
								/>
								{searchError && (
									<p className="text-red-500 text-xs mt-2">{searchError}</p>
								)}
							</form>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}
